import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage, useRotatingTagline } from '../i18n';
import { useSEO } from '../hooks/useSEO';
import { NotificationBell } from '../components/NotificationBell';
import { AuthModal, type AuthMode } from '../components/AuthModal';
import styles from './LoginPage.module.css';

/** Landing page for unauthenticated visitors.
 *
 *  Style mirrors polysansoft.com/polaris (hero -> how-it-works -> features ->
 *  platforms -> CTA -> legal footer) but in CSS modules instead of Tailwind.
 *  The login/register form lives in <AuthModal/>, triggered by nav "Giriş Yap"
 *  button and the hero CTAs. Legacy ?mode=login|register query params still
 *  auto-open the modal so any existing inbound links keep working.
 */
export function LoginPage() {
  const [searchParams] = useSearchParams();
  const { t, language, toggleLanguage } = useLanguage();
  const { tagline, isFading } = useRotatingTagline();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<AuthMode>('login');

  useSEO({
    title: 'Polaris - Trendyol Buy Box Optimizasyonu',
    description:
      'Polaris, Trendyol satıcıları için 7/24 otomatik Buy Box takibi, fiyat ' +
      'optimizasyonu, kupon farkındalığı ve rakip analizi. Demo için ücretsiz ' +
      'rapor alın.',
  });

  // Auto-open the modal when the URL signals an inbound auth flow:
  //   /login?mode=register     -> legacy direct register link
  //   /login?mode=login        -> legacy direct login link
  //   /login?redirect=<url>    -> bounced back from a protected page (e.g.
  //                               payment flow): pop login modal so the user
  //                               can sign in, then continue where they were.
  useEffect(() => {
    const m = searchParams.get('mode');
    const r = searchParams.get('redirect');
    if (m === 'register' || m === 'login') {
      setModalMode(m);
      setModalOpen(true);
    } else if (r) {
      setModalMode('login');
      setModalOpen(true);
    }
  }, [searchParams]);

  const redirectTo = searchParams.get('redirect') || undefined;

  const openLogin    = () => { setModalMode('login');    setModalOpen(true); };
  const openRegister = () => { setModalMode('register'); setModalOpen(true); };

  return (
    <div className={styles.page}>
      {/* ===== Sticky top nav ===== */}
      <nav className={styles.nav}>
        <div className={styles.navInner}>
          <a href="#top" className={styles.brand}>
            <img src="/polaris.svg" alt="Polaris" width="32" height="32" />
            <span>Polaris</span>
          </a>
          <div className={styles.navLinks}>
            <Link to="/payment-plans#top" className={styles.navLink}>{t.nav.pricing}</Link>
            <a href="#features" className={styles.navLink}>
              {language === 'tr' ? 'Özellikler' : 'Features'}
            </a>
            <Link to="/hakkimizda" className={styles.navLink}>{t.nav.about}</Link>
            <Link to="/help" className={styles.navLink}>{t.nav.help}</Link>
            <button onClick={toggleLanguage} className={styles.langBtn}>
              {language === 'tr' ? 'EN' : 'TR'}
            </button>
            <NotificationBell variant="floating" />
            <button onClick={openLogin} className={styles.navLoginBtn}>
              {t.auth.signIn}
            </button>
            <button onClick={openRegister} className={styles.navPrimaryBtn}>
              {language === 'tr' ? 'Ücretsiz Dene' : 'Try Free'}
            </button>
          </div>
        </div>
      </nav>

      {/* ===== Hero ===== */}
      <section id="top" className={styles.hero}>
        <div className={styles.heroBgImage} />
        <div className={styles.heroGradient} />
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <span className={styles.badgeDot}></span>
            <span>{language === 'tr' ? 'Polysan Yazılım ürünü' : 'A Polysan Yazılım product'}</span>
          </div>
          <h1 className={styles.heroTitle}>
            {language === 'tr'
              ? 'Trendyol\'da kaybettiğiniz'
              : 'Recover the Buy Boxes you\'re losing'}
            <br />
            <span className={styles.heroTitleAccent}>
              {language === 'tr' ? 'Buy Box\'ları geri kazanın' : 'on Trendyol'}
            </span>
          </h1>
          <p className={styles.heroSubtitle} style={{ opacity: isFading ? 0 : 1 }}>
            {tagline}
          </p>
          <p className={styles.heroDescription}>
            {language === 'tr'
              ? 'Polaris pazaryeri operasyonlarınızı 7/24 izler, rakip fiyat ve kupon hareketlerini gerçek zamanlı analiz eder, fiyatlarınızı Buy Box\'ı geri kazandıracak şekilde otomatik optimize eder.'
              : 'Polaris monitors your marketplace operations 24/7, analyzes competitor price and coupon movements in real time, and auto-optimizes your prices to recover the Buy Box.'}
          </p>
          <div className={styles.heroCtas}>
            <button onClick={openRegister} className={styles.heroPrimaryBtn}>
              {language === 'tr' ? 'Ücretsiz Hesap Aç' : 'Create Free Account'}
            </button>
            <Link to="/payment-plans#top" className={styles.heroSecondaryBtn}>
              {language === 'tr' ? 'Fiyatlandırmayı Gör' : 'See Pricing'}
            </Link>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.statItem}>
              <strong>50.000+</strong>
              <span>{language === 'tr' ? 'Trendyol mağazası analiz edildi' : 'Trendyol stores analyzed'}</span>
            </div>
            <div className={styles.statItem}>
              <strong>3.5M+</strong>
              <span>{language === 'tr' ? 'Ürün kupon-farkında izleniyor' : 'Coupon-aware product datapoints'}</span>
            </div>
            <div className={styles.statItem}>
              <strong>7/24</strong>
              <span>{language === 'tr' ? 'Kesintisiz fiyat takibi' : 'Continuous price monitoring'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Trust strip ===== */}
      <section className={styles.trustStrip}>
        <div className={styles.trustInner}>
          <div className={styles.trustItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
            <span>{language === 'tr' ? 'Garanti BBVA Sanal POS' : 'Garanti BBVA Virtual POS'}</span>
          </div>
          <div className={styles.trustItem}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            <span>{language === 'tr' ? 'Anında Aktivasyon' : 'Instant Activation'}</span>
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {language === 'tr' ? 'Nasıl Çalışır?' : 'How It Works'}
            </h2>
            <p className={styles.sectionSubtitle}>
              {language === 'tr'
                ? 'Üç adımda kurulum, sonra fiyatlandırma operasyonunuz Polaris\'e devredilir.'
                : 'Three-step setup, then your pricing operation is handed over to Polaris.'}
            </p>
          </div>

          <div className={styles.stepGrid}>
            <div className={styles.step}>
              <div className={`${styles.stepCircle} ${styles.circleBlue}`}>1</div>
              <h3 className={styles.stepTitle}>
                {language === 'tr' ? 'Entegrasyon' : 'Integration'}
              </h3>
              <p className={styles.stepText}>
                {language === 'tr'
                  ? 'Trendyol mağazanızı güvenli API bağlantısıyla entegre edin. Optimize etmek istediğiniz ürünleri seçin, gerisini bize bırakın.'
                  : 'Connect your Trendyol store via secure API. Pick the products to optimize, leave the rest to us.'}
              </p>
            </div>
            <div className={styles.step}>
              <div className={`${styles.stepCircle} ${styles.circlePurple}`}>2</div>
              <h3 className={styles.stepTitle}>
                {language === 'tr' ? 'Kural Tanımlama' : 'Define Rules'}
              </h3>
              <p className={styles.stepText}>
                {language === 'tr'
                  ? 'Ürün bazında minimum ve maksimum fiyat eşiklerini, hedef rekabet pozisyonunuzu ve marj koruma kurallarınızı belirleyin.'
                  : 'Set per-product price floors and ceilings, target competitive position, and margin protection rules.'}
              </p>
            </div>
            <div className={styles.step}>
              <div className={`${styles.stepCircle} ${styles.circleGreen}`}>3</div>
              <h3 className={styles.stepTitle}>
                {language === 'tr' ? 'Dinamik Optimizasyon' : 'Dynamic Optimization'}
              </h3>
              <p className={styles.stepText}>
                {language === 'tr'
                  ? 'Polaris pazar dinamiklerini sürekli analiz eder, fiyatlarınızı gerçek zamanlı optimize eder. Buy Box kazanım oranınızı sistematik olarak artırır.'
                  : 'Polaris continuously analyzes market dynamics, optimizes prices in real time, and systematically increases your Buy Box win rate.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section id="features" className={`${styles.section} ${styles.sectionAlt}`}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {language === 'tr' ? 'Platform Özellikleri' : 'Platform Capabilities'}
            </h2>
          </div>

          <div className={styles.featureGrid}>
            {featureList(language).map((f) => (
              <div key={f.title} className={styles.featureCard}>
                <div className={styles.featureIcon} style={{ background: f.bg, color: f.fg }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d={f.icon}/></svg>
                </div>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureText}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Supported platforms ===== */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {language === 'tr' ? 'Desteklenen Pazaryerleri' : 'Supported Marketplaces'}
            </h2>
            <p className={styles.sectionSubtitle}>
              {language === 'tr'
                ? 'Trendyol entegrasyonu aktif. Amazon, Hepsiburada ve N11 için geliştirme süreçleri devam ediyor.'
                : 'Trendyol integration is live. Amazon, Hepsiburada and N11 are in development.'}
            </p>
          </div>

          <div className={styles.platformGrid}>
            <div className={`${styles.platformCard} ${styles.platformActive}`}>
              <div className={styles.platformName}>Trendyol</div>
              <span className={`${styles.platformBadge} ${styles.badgeActive}`}>
                {language === 'tr' ? 'Kullanımda' : 'Live'}
              </span>
            </div>
            <div className={styles.platformCard}>
              <div className={styles.platformNameMuted}>Amazon</div>
              <span className={`${styles.platformBadge} ${styles.badgeComing}`}>
                {language === 'tr' ? 'Geliştirmede' : 'In Development'}
              </span>
            </div>
            <div className={styles.platformCard}>
              <div className={styles.platformNameMuted}>Hepsiburada</div>
              <span className={`${styles.platformBadge} ${styles.badgeComing}`}>
                {language === 'tr' ? 'Geliştirmede' : 'In Development'}
              </span>
            </div>
            <div className={styles.platformCard}>
              <div className={styles.platformNameMuted}>N11</div>
              <span className={`${styles.platformBadge} ${styles.badgeComing}`}>
                {language === 'tr' ? 'Geliştirmede' : 'In Development'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA banner ===== */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>
            {language === 'tr'
              ? 'Fiyatlandırma operasyonunuzu profesyonelleştirin.'
              : 'Professionalize your pricing operation.'}
          </h2>
          <p className={styles.ctaText}>
            {language === 'tr'
              ? 'Polaris ile pazaryeri operasyonlarınızda veri odaklı karar alma süreçlerine geçiş yapın. Bir tıkla başlayın.'
              : 'Move your marketplace operations to data-driven decisions with Polaris. Get started in one click.'}
          </p>
          <div className={styles.ctaBtns}>
            <button onClick={openRegister} className={styles.ctaPrimaryBtn}>
              {language === 'tr' ? 'Ücretsiz Hesap Aç' : 'Create Free Account'}
            </button>
            <Link to="/payment-plans#top" className={styles.ctaSecondaryBtn}>
              {language === 'tr' ? 'Fiyatlandırmayı Gör' : 'See Pricing'}
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Legal footer ===== */}
      <footer className={styles.legalFooter}>
        <div className={styles.footerInner}>
          <div className={styles.companyInfo}>
            <strong>{t.footer.companyName}</strong>
            <span className={styles.companyType}>{t.footer.companyType}</span>
            <span>{t.footer.address}</span>
            <span>
              <a href={`tel:${t.footer.phone.replace(/\s/g, '')}`}>{t.footer.phone}</a>
              {' | '}
              <a href={`mailto:${t.footer.email}`}>{t.footer.email}</a>
            </span>
          </div>
          <div className={styles.legalLinks}>
            <span className={styles.deliveryBadge}>{t.footer.instantDelivery}</span>
            <Link to="/privacy-policy">{t.footer.privacyPolicy}</Link>
            <Link to="/terms-of-service">{t.footer.termsOfService}</Link>
            <Link to="/return-policy">{t.footer.returnPolicy}</Link>
            <Link to="/cookie-policy">{t.footer.cookiePolicy}</Link>
          </div>
          <div className={styles.copyright}>
            © {new Date().getFullYear()} Polaris - {t.footer.aPolysanProduct}
          </div>
        </div>
      </footer>

      {/* ===== Auth modal ===== */}
      <AuthModal
        isOpen={modalOpen}
        initialMode={modalMode}
        onClose={() => setModalOpen(false)}
        redirectTo={redirectTo}
      />
    </div>
  );
}

/** Feature list — translations inline so the file stays self-contained.
 *  Icons are heroicon SVG path strings, rendered into the feature card. */
function featureList(language: 'tr' | 'en') {
  const isTR = language === 'tr';
  return [
    {
      title: isTR ? 'Stratejik Fiyat Belirleme' : 'Strategic Pricing',
      text:  isTR ? 'Tanımlı iş hedeflerine uygun optimal fiyat noktasını algoritmik olarak tespit eder. Kâr marjlarınızı korurken rekabet gücünüzü sürdürmenizi sağlar.'
                  : 'Algorithmically finds the optimal price point matching your business goals. Preserves margin while maintaining competitive edge.',
      icon:  'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      bg: '#dbeafe', fg: '#2563eb',
    },
    {
      title: isTR ? 'Buy Box Yönetimi' : 'Buy Box Management',
      text:  isTR ? 'Buy Box metriklerini sürekli analiz ederek kazanım olasılığını maksimize eden stratejiler uygular. Fiyat, indirim ve kupon parametrelerini koordine eder.'
                  : 'Continuously analyzes Buy Box metrics to maximize win rate. Coordinates price, discount and coupon parameters.',
      icon:  'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      bg: '#dcfce7', fg: '#16a34a',
    },
    {
      title: isTR ? '15 Dakikada Optimizasyon' : 'Optimization in 15 Minutes',
      text:  isTR ? 'Ürününüz aktifleştikten sonra 15 dakika içinde optimal fiyatı belirleriz. Her 15 dakikada bir rakip fiyat hareketlerini takip eder ve fiyatınızı güncelleriz.'
                  : 'We set the optimal price within 15 minutes of activating your product, then re-check competitor moves every 15 minutes.',
      icon:  'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.582m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
      bg: '#cffafe', fg: '#0891b2',
    },
    {
      title: isTR ? '7/24 Operasyon' : '24/7 Operation',
      text:  isTR ? 'Kesintisiz izleme ve müdahale kapasitesi. Pazar koşullarındaki değişimlere anlık tepki vererek fırsat kayıplarını önler.'
                  : 'Continuous monitoring and intervention capacity. Reacts instantly to market changes and prevents missed opportunities.',
      icon:  'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      bg: '#f3e8ff', fg: '#9333ea',
    },
    {
      title: isTR ? 'Kupon Farkındalığı' : 'Coupon Awareness',
      text:  isTR ? 'Rakiplerin aktif kuponlarını gerçek zamanlı izler, gerçek Buy Box skorunu kuponları hesaba katarak hesaplar. "Aynı fiyatta olduğumuz halde neden kaybediyoruz?" sorusunu yanıtlar.'
                  : 'Tracks competitors\' active coupons in real time, computes the true Buy Box score including coupon credits. Answers "why am I losing at the same price?"',
      icon:  'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
      bg: '#fed7aa', fg: '#ea580c',
    },
    {
      title: isTR ? 'Akıllı Minimum Koruması' : 'Smart Minimum Protection',
      text:  isTR ? 'Hata yapılsa bile fiyatı minimuma çekmez. Diğer otomatik sistemlerin aksine, fiyatı daha yukarıda tutabileceği durumlarda yukarıda tutar; körü körüne dibe inmez.'
                  : 'Even if there\'s an input mistake, we don\'t drop to minimum. Unlike other auto-pricers, when the price can hold higher, we hold it higher — no blind race to the bottom.',
      icon:  'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      bg: '#fef3c7', fg: '#d97706',
    },
    {
      title: isTR ? 'Stok Eritme Esnekliği' : 'Stock-Clearance Flexibility',
      text:  isTR ? 'Polaris\'te ürününüz aktifken bile fiyatı manuel olarak düşürerek stok eritebilirsiniz. Sistem müdahale etmez; istediğiniz an kontrolü siz alırsınız.'
                  : 'You can manually drop prices to clear stock while your product is active. Polaris won\'t fight you — take back control whenever you want.',
      icon:  'M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6',
      bg: '#fce7f3', fg: '#db2777',
    },
    {
      title: isTR ? 'Rekabetçi Ürünler İçin' : 'For Competitive Products',
      text:  isTR ? 'Fiyatı 1 TL artırdığınızda Buy Box\'ı kaybedebileceğiniz, yüksek rekabet barındıran ürünlerinizi sistemimize eklemenizi tavsiye ederiz. Polaris en çok orada işe yarar.'
                  : 'Add the high-competition products where raising price by 1 TL would cost you the Buy Box. That\'s where Polaris pays off most.',
      icon:  'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
      bg: '#ccfbf1', fg: '#0f766e',
    },
  ];
}
